<script setup>
import { computed } from "vue";
import { useStore } from "vuex";

const store = useStore();
const canAdd = computed(() => store.getters.hasPermission("expense.add"));
const canEdit = computed(() => store.getters.hasPermission("expense.edit"));
const canDelete = computed(() => store.getters.hasPermission("expense.delete"));
</script>

<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div v-if="canAdd || editingId" class="card">
          <div class="card-body">
            <div class="row">
              <div class="col-lg-4 col-sm-6 col-12">
                <div class="form-group">
                  <label>Category Name</label>
                  <input v-model.trim="form.categoryname" type="text" class="form-control" ref="firstInput" @keypress.enter="saveCategory" />
                </div>
              </div>
              <div class="col-lg-4 col-sm-6 col-12">
                <div class="form-group">
                  <label>Description</label>
                  <input v-model.trim="form.description" type="text" class="form-control" @keypress.enter="saveCategory" />
                </div>
              </div>
              <div class="col-lg-2 col-sm-6 col-12">
                <div class="form-group">
                  <label>Status</label>
                  <select v-model="form.is_active" class="form-control">
                    <option :value="true">Active</option>
                    <option :value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div class="col-lg-2 col-sm-6 col-12 d-flex align-items-end">
                <div class="form-group w-100">
                  <button type="button" class="btn btn-submit me-2" @click="saveCategory">{{ editingId ? "Update" : "Submit" }}</button>
                  <button v-if="editingId" type="button" class="btn btn-cancel" @click="resetForm">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <div class="table-top">
              <div class="search-set">
                <div class="search-input">
                  <a class="btn btn-searchset"><img src="../../../assets/img/icons/search-white.svg" alt="img" /></a>
                  <div class="dataTables_filter">
                    <label>
                      <input v-model="search" type="search" class="form-control form-control-sm" placeholder="Search..." />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div class="table-responsive">
              <a-table :columns="columns" :data-source="filteredCategories" row-key="categoryid">
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'status'">
                    <span class="badges" :class="record.is_active ? 'bg-lightgreen' : 'bg-lightred'">
                      {{ record.is_active ? "Active" : "Inactive" }}
                    </span>
                  </template>
                  <template v-else-if="column.key === 'action' && (canEdit || canDelete)">
                    <a v-if="canEdit" class="me-3" href="javascript:void(0);" @click="editCategory(record)">
                      <img src="../../../assets/img/icons/edit.svg" alt="img" />
                    </a>
                    <a v-if="canDelete" class="me-3 confirm-text" href="javascript:void(0);" @click="deleteCategory(record.categoryid)">
                      <img src="../../../assets/img/icons/delete.svg" alt="img" />
                    </a>
                  </template>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const columns = [
  { title: "Category Name", dataIndex: "categoryname", sorter: (a, b) => a.categoryname.localeCompare(b.categoryname) },
  { title: "Location", dataIndex: "locationname", sorter: (a, b) => (a.locationname || "").localeCompare(b.locationname || "") },
  { title: "Description", dataIndex: "description", sorter: (a, b) => (a.description || "").localeCompare(b.description || "") },
  { title: "Status", key: "status" },
  { title: "Action", key: "action" },
];

export default {
  data() {
    return {
      title: "Expense Categories",
      title1: "Manage expense category masters",
      categories: [],
      search: "",
      editingId: null,
      form: {
        categoryname: "",
        description: "",
        is_active: true,
      },
      columns,
    };
  },
  computed: {
    filteredCategories() {
      const term = this.search.toLowerCase();
      return this.categories.filter((item) => {
        return !term ||
          item.categoryname.toLowerCase().includes(term) ||
          (item.description || "").toLowerCase().includes(term) ||
          (item.locationname || "").toLowerCase().includes(term);
      });
    },
  },
  methods: {
    resetForm() {
      this.editingId = null;
      this.form = { categoryname: "", description: "", is_active: true };
      this.$nextTick(() => this.$refs.firstInput?.focus());
    },
    editCategory(category) {
      this.editingId = category.categoryid;
      this.form = {
        categoryname: category.categoryname,
        description: category.description || "",
        is_active: category.is_active,
      };
      this.$nextTick(() => this.$refs.firstInput?.focus());
    },
    async saveCategory() {
      if (!this.form.categoryname) {
        Swal.fire("Please enter category name");
        return;
      }
      try {
        if (this.editingId) {
          await axios.put(`/v1/expenses/categories/${this.editingId}`, this.form);
          Swal.fire("Expense category updated successfully");
        } else {
          await axios.post("/v1/expenses/categories", this.form);
          Swal.fire("Expense category created successfully");
        }
        this.resetForm();
        await this.loadCategories();
      } catch (error) {
        Swal.fire(error.response?.data?.detail || "Unable to save expense category");
      }
    },
    async deleteCategory(categoryid) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This category will be deleted if it is not used by expenses.",
        showCancelButton: true,
        confirmButtonColor: "#FF9F43",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });
      if (!result.isConfirmed) return;
      try {
        await axios.delete(`/v1/expenses/categories/${categoryid}`);
        Swal.fire("Deleted!", "Expense category has been deleted.");
        await this.loadCategories();
      } catch (error) {
        Swal.fire(error.response?.data?.detail || "Unable to delete expense category");
      }
    },
    async loadCategories() {
      const response = await axios.get("/v1/expenses/categories", { params: { include_inactive: true } });
      this.categories = response.data || [];
    },
  },
  mounted() {
    this.loadCategories();
    this.$nextTick(() => this.$refs.firstInput?.focus());
  },
  name: "expensecategory",
};
</script>
